import type { LabOrder, LabTracking } from "@/types/labs";

/** Supplier panel codes are routing identifiers, not patient-facing names. */
export function labDisplayName(name: string): string {
  return (
    name.replace(/\s*[·–—-]\s*Y\d{3}\b/gi, "").trim() || "At-home blood test"
  );
}

export function labNeedsAttention(order: LabOrder): boolean {
  return Boolean(
    order.issue ||
    order.recollection_message ||
    order.authorization_status === "declined" ||
    [
      "awaiting_authorization",
      "delayed",
      "problem",
      "error",
      "returned",
      "rejected",
      "failed",
    ].includes(order.fulfillment_status) ||
    order.bundles.some((bundle) =>
      [
        "delayed",
        "problem",
        "error",
        "returned",
        "rejected",
        "failed",
      ].includes(bundle.status),
    ) ||
    order.results.some((result) =>
      ["quality_issue", "unrecognized"].includes(result.quality_status ?? ""),
    ),
  );
}

export function labHasResults(order: LabOrder): boolean {
  return order.results.some(
    (result) => result.report_available || result.observations.length > 0,
  );
}

export function labIsArchived(order: LabOrder): boolean {
  return (
    ["cancelled", "deleted"].includes(order.fulfillment_status) ||
    ["refunded", "cancelled"].includes(order.payment_status)
  );
}

const journeySteps: Record<string, number> = {
  accepted: 0,
  pending: 0,
  pendingFulfillment: 0,
  inLogistics: 0,
  readyToShip: 0,
  awaitingShipment: 0,
  dispatch_pending: 0,
  dispatching: 0,
  dispatch_uncertain: 0,
  inTransitToPatient: 1,
  atPatient: 2,
  inTransitToLab: 2,
  atLab: 3,
  resultsReady: 3,
};

/** Never imply fulfillment on a failed payment, unknown status or collection issue. */
export function labJourneyStep(order: LabOrder): number | null {
  if (
    !["paid", "succeeded"].includes(order.payment_status) ||
    labNeedsAttention(order) ||
    ["cancelled", "deleted"].includes(order.fulfillment_status)
  )
    return null;
  if (order.bundles.length) {
    const steps = order.bundles.map((bundle) => journeySteps[bundle.status]);
    if (steps.some((step) => step === undefined)) return null;
    const leastAdvanced = Math.min(...steps);
    if (leastAdvanced < 3) return leastAdvanced;
  }
  if (labHasResults(order)) return 4;
  return order.bundles.length
    ? Math.min(...order.bundles.map((bundle) => journeySteps[bundle.status]))
    : (journeySteps[order.fulfillment_status] ?? null);
}

export function featuredLabOrder(orders: LabOrder[]): LabOrder | undefined {
  const recent = [...orders].sort(
    (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
  );
  const active = recent.filter(
    (order) =>
      !["cancelled", "deleted", "failed", "rejected"].includes(
        order.fulfillment_status,
      ) && !["refunded", "cancelled", "failed"].includes(order.payment_status),
  );
  return (
    active.find(labNeedsAttention) ??
    active.find((order) => !labHasResults(order)) ??
    active[0] ??
    recent[0]
  );
}

export function labHomeCopy(order?: LabOrder) {
  if (!order)
    return {
      title: "Understand your baseline.",
      description:
        "Explore at-home blood testing. Follow your kit and keep your laboratory reports together in Labs.",
      action: "Explore lab tests",
      to: "/app/shop?tab=labs",
    };
  const to = `/app/labs/${encodeURIComponent(order.id)}`;
  if (labNeedsAttention(order))
    return {
      title: "Your test needs attention.",
      description:
        "Open your test for the latest update and any next steps from your care team.",
      action: "View next steps",
      to,
    };
  if (
    !["paid", "succeeded"].includes(order.payment_status) ||
    ["cancelled", "deleted"].includes(order.fulfillment_status)
  )
    return {
      title: "Your blood test update.",
      description: labOrderLabel(order),
      action: "View your test",
      to,
    };
  if (labHasResults(order))
    return {
      title: "Your results are available.",
      description:
        "Review the measurements and original report supplied by the laboratory.",
      action: "View results",
      to: `${to}#results`,
    };
  const step = labJourneyStep(order);
  if (step === 1)
    return {
      title: "Your kit is on its way.",
      description: "Follow your delivery and return here for the next step.",
      action: "Track your kit",
      to,
    };
  if (step === 2)
    return {
      title:
        order.fulfillment_status === "inTransitToLab" ||
        order.bundles.some((bundle) => bundle.status === "inTransitToLab")
          ? "Your sample is on its way."
          : "Your kit has arrived.",
      description:
        "Follow the collection and return instructions included in your kit. Track its return to the laboratory in Labs.",
      action: "View kit progress",
      to,
    };
  if (step === 3)
    return {
      title: "Your laboratory update.",
      description:
        "Your sample is with the laboratory. Your results will appear when the report is available.",
      action: "View test status",
      to,
    };
  return {
    title: "Your blood test is underway.",
    description:
      "Follow kit preparation, delivery and your laboratory report in one place.",
    action: "View kit progress",
    to,
  };
}

export function labProductImage(panelCode: string): string | null {
  return panelCode === "Y499"
    ? "/images/tasso/system-collection-device-transparent.png"
    : null;
}

const statuses: Record<string, string> = {
  accepted: "Order received",
  pendingFulfillment: "Preparing your kit",
  inLogistics: "Preparing your kit",
  readyToShip: "Ready to ship",
  awaitingShipment: "Awaiting shipment",
  inTransitToPatient: "Kit on its way",
  atPatient: "Kit delivered",
  inTransitToLab: "Sample returning to the lab",
  atLab: "Sample received by the lab",
  resultsReady: "Results received by Tasso",
  delayed: "Delivery delayed",
  problem: "Order needs attention",
  error: "Order needs attention",
  returned: "Package returned",
  rejected: "Order could not be completed",
  failed: "Order could not be completed",
  cancelled: "Cancelled",
  deleted: "Cancelled",
  pending: "Preparing your order",
  awaiting_authorization: "Order needs attention",
  dispatch_pending: "Preparing your kit",
  dispatching: "Preparing your kit",
  dispatch_uncertain: "Confirming kit order",
};

export function labStatusLabel(status: string): string {
  return statuses[status] ?? "Checking order progress";
}

export function labOrderLabel(order: LabOrder): string {
  if (order.authorization_status === "declined") return "Order needs attention";
  if (order.payment_status !== "paid" && order.payment_status !== "succeeded") {
    return order.payment_status === "refunded"
      ? "Refunded"
      : "Confirming payment";
  }
  if (order.recollection_message) return "New sample may be needed";
  if (labNeedsAttention(order)) return "Order needs attention";
  if (labHasResults(order)) return "Lab results available";
  if (order.fulfillment_status === "resultsReady")
    return "Retrieving your lab results";
  return labStatusLabel(order.fulfillment_status);
}

export function labDate(value: string | null | undefined): string {
  if (!value) return "Not yet available";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Not yet available";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    // Collection dates may be calendar-only. They must not shift into the
    // prior day when viewed west of UTC.
    ...(/^\d{4}-\d{2}-\d{2}$/.test(value) ? { timeZone: "UTC" } : {}),
  });
}

export function labMoney(cents: number, currency = "usd"): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(
    cents / 100,
  );
}

/** Tracking links are an affordance, never a way to execute vendor content. */
export function labTrackingUrl(tracking: LabTracking): string | null {
  if (tracking.tracking_url) {
    try {
      const url = new URL(tracking.tracking_url);
      if (url.protocol === "https:") return url.href;
    } catch {
      /* display the tracking number when the URL is malformed */
    }
  }
  return null;
}

export function labReportUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
}
